import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { LogOut, User } from 'lucide-react'
import { useAnalytics } from 'hooks'
import { useAppSelector, useAppDispatch } from 'modules/core'
import { selectCurrentUserId } from 'modules/common'
import { selectUserById, logoutAction } from 'modules/authentication/slices'
import { cn } from 'lib/utils'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  dropdownItemDarkClass,
} from 'components/DropdownMenu'

interface UserProfileFooterProps {
  onNavigate?: (path: string) => void
}

export function UserProfileFooter({ onNavigate }: UserProfileFooterProps) {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { track } = useAnalytics()
  const userId = useAppSelector(selectCurrentUserId)
  const user = useAppSelector(state => (userId ? selectUserById(state, userId) : undefined))

  const handleLogout = useCallback(() => {
    track(AnalyticsEvents.Authentication.LOGOUT, { user_id: userId ?? '' })
    dispatch(logoutAction())
    navigate('/login')
  }, [dispatch, navigate, track, userId])

  const name = user?.name || 'User'
  const email = user?.email || ''
  const avatarUrl = user?.avatar?.publicUrl
  const initials = name
    .split(' ')
    .slice(0, 2)
    .map(w => w[0])
    .join('')
    .toUpperCase()

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex w-full items-center gap-2.5 rounded-[8px] px-3 py-2.5 text-left transition-colors hover:bg-bg-3">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={name}
              className="h-7 w-7 flex-shrink-0 rounded-full border border-line-bright object-cover"
            />
          ) : (
            <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border border-line-bright bg-bg-3 text-[10px] font-semibold text-paper-dim">
              {initials}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="truncate text-[13px] font-medium text-paper">{name}</p>
            {email && <p className="truncate text-[11px] text-paper-faint">{email}</p>}
          </div>
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent variant="dark" align="start" side="top" className="w-52" sideOffset={4}>
        <DropdownMenuItem
          onClick={() => (onNavigate ? onNavigate('/profile') : navigate('/profile'))}
          className={cn('cursor-pointer gap-2', dropdownItemDarkClass)}
        >
          <User size={14} />
          <span className="text-[13px]">Profile</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator className="my-1 h-px bg-line" />
        <DropdownMenuItem
          onClick={handleLogout}
          className="cursor-pointer gap-2 text-[color:var(--alert-error-text)] data-[highlighted]:bg-[color:var(--alert-error-bg)]"
        >
          <LogOut size={14} />
          <span className="text-[13px]">Log out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
